/**
 * Default input bindings for every scheme, and the controls reference built
 * from them. Fixed for now (no rebinding UI); the joystick's *axes* are the one
 * thing a player can reassign, because non-standard sticks report them at
 * different indices per browser (see DeviceInput).
 *
 * Actions come in two kinds. A *press* action fires once (gear, flaps, camera
 * toggles); a *hold* action is on for as long as any source holds it (fire,
 * flares, brakes, zoom) — sources are tracked separately so letting go of the
 * gamepad trigger doesn't stop a gun the mouse is still firing.
 */

/** Actions that are held rather than pressed. */
export const HOLD_ACTIONS = new Set(['fire', 'zoom', 'flares', 'brake', 'highlight', 'look']);

/** Actions still live on a destroyed airframe — view and pause only. */
export const WRECK_ACTIONS = new Set(['cameraToggle', 'enemyCamera', 'pause', 'menu', 'zoom']);

/** Actions still live while the sim is paused — the view, and unpausing. */
export const PAUSED_ACTIONS = new Set(['cameraToggle', 'enemyCamera', 'actionCamera', 'pause', 'menu', 'zoom', 'look', 'highlight']);

// ── Keyboard ─────────────────────────────────────────────────────────────────
// Keyed by `KeyboardEvent.code` — a physical position, so WASD stays under the
// left hand on AZERTY and friends.
export const KEY_ACTIONS = {
  KeyI: 'engine',
  KeyF: 'flaps',
  KeyG: 'gear',
  KeyT: 'weapon',
  KeyN: 'nvg',
  KeyB: 'brake',
  KeyV: 'flares',
  KeyX: 'enemyCamera',
  KeyC: 'actionCamera',
  Enter: 'cameraToggle',
  NumpadEnter: 'cameraToggle',
  KeyP: 'pause',
  Space: 'look',
};

/** Flight-axis keys → [axis, direction] in pilot sense (pitch + = pull). */
export const AXIS_KEYS = {
  KeyW: ['pitch', -1],
  KeyS: ['pitch', 1],
  KeyA: ['roll', -1],
  KeyD: ['roll', 1],
  KeyQ: ['yaw', -1],
  KeyE: ['yaw', 1],
};

/** Throttle keys — live only with the keyboard locked (see keyboardLock.js). */
export const THROTTLE_KEYS = {
  ShiftLeft: 1,
  ShiftRight: 1,
  ControlLeft: -1,
  ControlRight: -1,
};

// ── Gamepad (Gamepad API "standard" mapping — Xbox layout) ───────────────────
// `hold`: held action. `tap`: press action. `long`: press action after holding
// for CONTROLS.gamepad.holdTime — a button with both fires `tap` on release.
// Axes, the bumpers (rudder) and the D-pad up/down (throttle) are read
// directly by the controller.
export const GAMEPAD_BUTTONS = {
  0: { tap: 'weapon' },                       // A
  1: { hold: 'flares' },                      // B
  2: { tap: 'flaps' },                        // X
  3: { tap: 'gear' },                         // Y
  6: { hold: 'zoom' },                        // LT
  7: { hold: 'fire' },                        // RT
  8: { tap: 'actionCamera', long: 'engine' }, // View
  9: { tap: 'menu' },                         // Menu
  10: { tap: 'enemyCamera', long: 'nvg' },    // L3
  11: { tap: 'cameraToggle' },                // R3
  14: { hold: 'brake' },                      // D-pad left
  15: { hold: 'highlight' },                  // D-pad right
};
export const GAMEPAD_RUDDER = { left: 4, right: 5 };     // LB / RB
export const GAMEPAD_THROTTLE = { up: 12, down: 13 };    // D-pad up / down

// ── Joystick (Logitech Extreme 3D Pro button numbering, 0-based) ─────────────
export const JOYSTICK_BUTTONS = {
  0: { hold: 'fire' },          // 1 — trigger
  1: { hold: 'zoom' },          // 2 — thumb
  2: { tap: 'weapon' },         // 3
  3: { hold: 'flares' },        // 4
  4: { hold: 'brake' },         // 5
  5: { hold: 'highlight' },     // 6
  6: { tap: 'gear' },           // 7
  7: { tap: 'flaps' },          // 8
  8: { tap: 'cameraToggle' },   // 9
  9: { tap: 'actionCamera' },   // 10
  10: { tap: 'enemyCamera' },   // 11
  11: { long: 'engine' },       // 12
};

// ── Controls reference (ControlsOverlay) ─────────────────────────────────────

const SYSTEM_KEYS = [
  ['I', 'Engine start / stop'],
  ['F', 'Cycle flaps (up / half / full)'],
  ['G', 'Landing gear up / down (airborne only)'],
  ['B (hold)', 'Brakes — airbrake in flight, wheel brakes on the ground'],
];
const COMBAT_KEYS = [
  ['Left mouse', 'Fire selected weapon (hold for the gun)'],
  ['T / Middle mouse', 'Cycle weapon — gun, AIM-9, AGM-65'],
  ['Right mouse (hold)', 'Zoom — takes the Maverick seeker when AGM-65 is up'],
  ['V (hold)', 'Release countermeasure flares'],
  ['N', 'Night vision goggles (night missions)'],
];
const VIEW_KEYS = [
  ['Enter', 'Toggle cockpit / chase — and back from the views below'],
  ['C', 'Cycle action cameras — fixed mounts on the airframe'],
  ['X', 'Cycle enemy camera — mouse orbits, wheel zooms'],
];
const SYSTEM_ROWS = [
  ['Click canvas', 'Engage pointer lock'],
  ['P', 'Pause / unpause'],
  ['Esc', 'Release mouse, then pause menu'],
  ['R', 'Restart or continue after a crash or mission end'],
];
const FLIGHT_KEYS = [
  ['W / S', 'Pitch down / up'],
  ['A / D', 'Roll left / right (steers on the ground)'],
  ['Q / E', 'Yaw left / right'],
  ['Scroll wheel', 'Throttle 0–100%'],
  ['Shift / Ctrl', 'Throttle up / down — fullscreen only (Settings → Controls)'],
];

const REFERENCE = {
  basic: [
    ['Flight', [
      ['Mouse', 'Look / aim — the autopilot banks to follow'],
      ['W / S', 'Pitch — overrides the autopilot while held'],
      ['A / D', 'Roll — overrides the autopilot while held'],
      ['Q / E', 'Yaw — overrides the autopilot while held'],
      ['Scroll wheel', 'Throttle 0–100%'],
      ['Shift / Ctrl', 'Throttle up / down — fullscreen only (Settings → Controls)'],
      ['Space (hold)', 'Freelook — look around without turning; highlights targets'],
      ...SYSTEM_KEYS,
    ]],
    ['Combat', COMBAT_KEYS],
    ['View', VIEW_KEYS],
    ['System', SYSTEM_ROWS],
  ],
  keyboard: [
    ['Flight', [
      ...FLIGHT_KEYS,
      ['Mouse', 'Look around the cockpit'],
      ['Space (hold)', 'Highlight targets'],
      ...SYSTEM_KEYS,
    ]],
    ['Combat', COMBAT_KEYS],
    ['View', VIEW_KEYS],
    ['System', SYSTEM_ROWS],
  ],
  mouse: [
    ['Flight', [
      ['Mouse', 'Control stick — stays where you leave it (the HUD ring shows it)'],
      ...FLIGHT_KEYS,
      ['Space (hold)', 'Look around with the mouse; highlights targets'],
      ...SYSTEM_KEYS,
    ]],
    ['Combat', COMBAT_KEYS],
    ['View', VIEW_KEYS],
    ['System', SYSTEM_ROWS],
  ],
  gamepad: [
    ['Flight', [
      ['Left stick', 'Pitch and roll (steers on the ground)'],
      ['LB / RB', 'Rudder left / right'],
      ['D-pad up / down', 'Throttle (seeker zoom while aiming a Maverick)'],
      ['D-pad left (hold)', 'Brakes'],
      ['Right stick', 'Look around (slews the Maverick seeker while aiming)'],
      ['X', 'Cycle flaps'],
      ['Y', 'Landing gear'],
      ['View (hold)', 'Engine start / stop'],
    ]],
    ['Combat', [
      ['RT', 'Fire selected weapon'],
      ['LT (hold)', 'Zoom — takes the Maverick seeker when AGM-65 is up'],
      ['A', 'Cycle weapon'],
      ['B (hold)', 'Release countermeasure flares'],
      ['D-pad right (hold)', 'Highlight targets'],
      ['L3 (hold)', 'Night vision goggles'],
    ]],
    ['View', [
      ['R3', 'Toggle cockpit / chase'],
      ['View (tap)', 'Cycle action cameras'],
      ['L3 (tap)', 'Cycle enemy camera'],
    ]],
    ['System', [
      ['Menu', 'Pause / resume — restart or continue after a crash or mission end'],
      ['Mouse', 'Optional — looks around once the pointer is captured'],
      ['Keyboard', 'Every keyboard binding still works'],
    ]],
  ],
  joystick: [
    ['Flight', [
      ['Stick', 'Pitch and roll (steers on the ground)'],
      ['Twist', 'Rudder'],
      ['Throttle slider', 'Throttle — takes over from the wheel when moved'],
      ['Hat', 'Look around (slews the Maverick seeker while aiming)'],
      ['5 (hold)', 'Brakes'],
      ['7', 'Landing gear'],
      ['8', 'Cycle flaps'],
      ['12 (hold)', 'Engine start / stop'],
    ]],
    ['Combat', [
      ['Trigger', 'Fire selected weapon'],
      ['2 — thumb (hold)', 'Zoom — takes the Maverick seeker when AGM-65 is up'],
      ['3', 'Cycle weapon'],
      ['4 (hold)', 'Release countermeasure flares'],
      ['6 (hold)', 'Highlight targets'],
    ]],
    ['View', [
      ['9', 'Toggle cockpit / chase'],
      ['10', 'Cycle action cameras'],
      ['11', 'Cycle enemy camera'],
    ]],
    ['System', [
      ['P / Esc', 'Pause'],
      ['Mouse', 'Optional — looks around, or slews the Maverick seeker'],
      ['Keyboard', 'Every keyboard binding still works'],
      ['Settings → Controls', 'Assign axes if the stick, twist or slider read wrong'],
    ]],
  ],
};

/** The controls reference for a scheme: `[groupTitle, [[key, action], …]][]`. */
export function controlsReference(scheme) {
  return REFERENCE[scheme] || REFERENCE.basic;
}
